import { trackGaEvent, trackPageView as sendGa4PageView } from "@/lib/analytics";

// ─── Core track function ─────────────────────────────────────
let lastPageUrl = "";
let lastPageTime = 0;

export async function trackEvent(
  eventType: string,
  eventName?: string,
  extras?: Record<string, unknown>
) {
  const pageUrl = typeof window !== "undefined" ? window.location.pathname + window.location.search : "";

  trackGaEvent(eventType, {
    event_name: eventName || undefined,
    page_url: pageUrl,
    ...(extras || {}),
  });
}

// ─── Scroll depth (reset on each SPA navigation) ────────────
let scrollDepths = new Set<number>();

// ─── Page View (SPA): GA4 page_path + custom events for engagement ───
export function trackPageView(pageName?: string) {
  const pageUrl = window.location.pathname + window.location.search;
  const now = Date.now();

  if (lastPageUrl && lastPageTime) {
    const duration = now - lastPageTime;
    trackEvent("page_duration", lastPageUrl, { durationMs: duration });
  }

  lastPageUrl = pageUrl;
  lastPageTime = now;
  scrollDepths = new Set();

  sendGa4PageView(pageName || pageUrl);

  const navParams: Record<string, string | number | boolean | null> = {
    page_path: pageUrl,
  };
  if (typeof document !== "undefined" && document.title) {
    navParams.page_title = document.title;
  }
  trackGaEvent("spa_navigation", navParams);
}

// ─── Click Tracking ────────────────────────────────────────────
export function trackClick(
  elementId: string,
  elementText?: string,
  sectionId?: string,
  metadata?: Record<string, unknown>
) {
  trackEvent("click", elementId, {
    elementId,
    elementText: elementText || null,
    sectionId: sectionId || null,
    ...metadata,
  });
}

// ─── Section Visibility ──────────────────────────────────────
export function trackSectionView(sectionId: string, sectionName?: string) {
  trackEvent("section_view", sectionName || sectionId, { sectionId });
}

// ─── CTA / Conversion Events ─────────────────────────────────
export function trackCTA(ctaName: string, destination?: string) {
  trackEvent("cta_click", ctaName, { destination: destination || null });
}

export function trackConversion(eventName: string, value?: number, currency?: string) {
  trackEvent("conversion", eventName, { value, currency });
}

// ─── Form Events ─────────────────────────────────────────────
export function trackFormStart(formId: string, formName?: string) {
  trackEvent("form_start", formName || formId, { formId });
}

export function trackFormSubmit(formId: string, formName?: string, success?: boolean) {
  trackEvent("form_submit", formName || formId, { formId, success });
}

export function trackFormFieldFocus(formId: string, fieldName: string) {
  trackEvent("form_field_focus", `${formId}.${fieldName}`, { formId, fieldName });
}

// ─── Scroll Depth ────────────────────────────────────────────
export function initScrollDepthTracking(thresholds: number[] = [25, 50, 75, 90, 100]) {
  const onScroll = () => {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const docHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
    if (!docHeight) return;
    const percent = Math.round((scrollTop / docHeight) * 100);

    thresholds.forEach((t) => {
      if (percent >= t && !scrollDepths.has(t)) {
        scrollDepths.add(t);
        trackEvent("scroll_depth", `${t}%`);
      }
    });
  };

  window.addEventListener("scroll", onScroll, { passive: true });
  return () => window.removeEventListener("scroll", onScroll);
}

// ─── Time on Page (heartbeat) ─────────────────────────────────
export function initTimeOnPageTracking(intervalMs: number = 30000) {
  let seconds = 0;
  const interval = setInterval(() => {
    seconds += intervalMs / 1000;
    trackEvent("time_on_page", `${Math.floor(seconds)}s`, { seconds });
  }, intervalMs);
  return () => clearInterval(interval);
}

// ─── Section Visibility Observer ──────────────────────────────
export function initSectionVisibilityObserver(
  sectionIds: string[],
  onView?: (id: string) => void
) {
  const seen = new Set<string>();
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !seen.has(entry.target.id)) {
          seen.add(entry.target.id);
          trackSectionView(entry.target.id);
          if (onView) onView(entry.target.id);
        }
      });
    },
    { threshold: 0.3 }
  );

  sectionIds.forEach((id) => {
    const el = document.getElementById(id);
    if (el) observer.observe(el);
  });

  return () => observer.disconnect();
}

// ─── Navbar Link Tracking ────────────────────────────────────
export function initNavbarTracking() {
  const handleClick = (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    const link = target.closest("a");
    if (!link) return;
    const href = link.getAttribute("href") || "";
    const text = link.textContent?.trim() || "";
    trackClick("navbar_link", text, "navbar", { href });
  };
  document.addEventListener("click", handleClick);
  return () => document.removeEventListener("click", handleClick);
}

// ─── Footer Link Tracking ─────────────────────────────────────
export function initFooterTracking() {
  const footer = document.getElementById("site-footer");
  if (!footer) return () => {};

  const handleClick = (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    const link = target.closest("a");
    if (!link) return;
    const href = link.getAttribute("href") || "";
    const text = link.textContent?.trim() || "";
    trackClick("footer_link", text, "footer", { href });
  };
  footer.addEventListener("click", handleClick);
  return () => footer.removeEventListener("click", handleClick);
}

// ─── Store Button (App Store / Play Store) Tracking ──────────
export function trackStoreClick(store: "google_play" | "app_store", location: string) {
  trackEvent("store_click", store, { store, location });
}

// ─── Social Share Tracking ───────────────────────────────────
export function trackSocialShare(platform: string, contentId?: string) {
  trackEvent("social_share", platform, { contentId });
}

// ─── Error Tracking ────────────────────────────────────────────
export function trackError(errorMessage: string, errorType?: string, stack?: string) {
  trackEvent("error", errorType || "javascript_error", {
    errorMessage,
    stack: stack || null,
    url: window.location.href,
  });
}

// ─── Search Tracking ───────────────────────────────────────────
export function trackSearch(query: string, resultsCount?: number) {
  trackEvent("search", query, { query, resultsCount });
}

// ─── Video / Media Tracking ──────────────────────────────────
export function trackVideoPlay(videoId: string, videoName?: string) {
  trackEvent("video_play", videoName || videoId, { videoId });
}

export function trackVideoProgress(videoId: string, percent: number) {
  trackEvent("video_progress", `${percent}%`, { videoId, percent });
}

function flushPageDurationOnUnload() {
  if (!lastPageUrl || !lastPageTime) return;
  const duration = Date.now() - lastPageTime;
  trackEvent("page_duration", lastPageUrl, { durationMs: duration, unload: true });
}

// ─── Setup all tracking (call once after GA init; route changes use trackPageView from router) ───
export function setupAnalytics() {
  const cleanupScroll = initScrollDepthTracking();
  const cleanupTime = initTimeOnPageTracking();
  const cleanupNav = initNavbarTracking();

  let cleanupFooter: (() => void) | undefined;
  const footerTimeoutId = window.setTimeout(() => {
    cleanupFooter = initFooterTracking();
  }, 1000);

  window.addEventListener("beforeunload", flushPageDurationOnUnload);

  return () => {
    cleanupScroll();
    cleanupTime();
    cleanupNav();
    window.clearTimeout(footerTimeoutId);
    cleanupFooter?.();
    window.removeEventListener("beforeunload", flushPageDurationOnUnload);
  };
}